/**
 * Stage 2 — Extract.
 *
 * Sequentially extract pending files. The user can stop after current,
 * retry failures individually, or continue to review when at least one
 * file has succeeded.
 */

import { getState, setState, updateFile } from '../state.js';
import { el, clear, toast } from './dom.js';
import { extractInvoice } from '../lib/anthropic.js';
import { applyRulesToFiles } from '../lib/rules.js';
import { saveBatchSnapshot } from '../persistence.js';
import { formatBytes } from '../folders.js';
import { getSessionApiKey } from '../lib/crypto.js';

let mounted = null;
let cancelRequested = false;

export function renderExtractStage(mount) {
  clear(mount);
  mounted = mount;
  cancelRequested = false;

  mount.appendChild(buildView());

  const state = getState();
  const hasPending = state.files.some(f => f.status === 'pending');
  if (hasPending && !state.isExtracting) runExtraction();
}

function buildView() {
  const state = getState();
  const wrap = el('div');

  const done    = state.files.filter(f => f.status === 'done').length;
  const failed  = state.files.filter(f => f.status === 'failed').length;
  const pending = state.files.filter(f => f.status === 'pending').length;
  const working = state.files.find(f => f.status === 'working');
  const total   = state.files.length;
  const completed = done + failed;
  const pct = total === 0 ? 0 : Math.round((completed / total) * 100);

  wrap.appendChild(el('div', { class: 'path-bar' }, [
    el('div', {}, [
      el('span', { class: 'label' }, ['Extracting']),
      el('span', { class: 'path' }, [state.workName || ''])
    ]),
    el('span', { class: 'meta' }, [`${completed} / ${total}`])
  ]));

  // Progress
  const progressPanel = el('div', { class: 'panel', style: { marginBottom: '14px' } }, [
    el('div', { class: 'progress-track' }, [
      el('div', { class: 'progress-fill', style: { width: `${pct}%` } })
    ]),
    el('div', { class: 'progress-meta' }, [
      el('span', {}, [
        working
          ? `Currently: ${working.name}`
          : pending > 0 ? 'Starting…' : 'Done.'
      ]),
      el('span', {}, [`${pct}% complete`])
    ])
  ]);
  wrap.appendChild(progressPanel);

  const list = el('div', { class: 'queue-list' });
  for (const f of state.files) list.appendChild(renderRow(f));
  wrap.appendChild(list);

  wrap.appendChild(el('div', { class: 'batch-summary' }, [
    el('div', { class: 'summary-counts' }, [
      el('div', { class: 'count' }, [
        el('span', { class: 'num', style: { color: 'var(--success)' } }, [String(done)]),
        el('span', { class: 'lbl' }, ['done'])
      ]),
      failed > 0 && el('div', { class: 'count' }, [
        el('span', { class: 'num', style: { color: 'var(--danger)' } }, [String(failed)]),
        el('span', { class: 'lbl' }, ['failed'])
      ]),
      el('div', { class: 'count' }, [
        el('span', { class: 'num' }, [String(pending)]),
        el('span', { class: 'lbl' }, ['pending'])
      ])
    ]),
    el('div', { class: 'summary-actions' }, [
      state.isExtracting && el('button', {
        class: 'ghost-btn',
        onClick: () => { cancelRequested = true; toast('Will stop after current file.'); }
      }, ['Stop after current']),
      done > 0 && el('button', {
        class: 'primary-btn',
        onClick: () => setState({ stage: 'review' })
      }, ['Continue to review →'])
    ])
  ]));

  return wrap;
}

function renderRow(f) {
  const isWorking = f.status === 'working';
  const isFailed  = f.status === 'failed';
  const row = el('div', {
    class: `queue-row${isWorking ? ' is-working' : ''}${isFailed ? ' is-failed' : ''}`,
    'data-file-id': f.id
  });

  const thumbContent = f.thumbDataUrl
    ? el('img', { src: f.thumbDataUrl, alt: '' })
    : document.createTextNode('PDF');
  row.appendChild(el('div', { class: 'thumb' }, [thumbContent]));

  const summary = makeSummary(f);
  row.appendChild(el('div', { style: { minWidth: '0' } }, [
    el('div', { class: 'fname', title: f.name }, [f.name]),
    el('div', { class: 'fmeta', title: summary }, [summary])
  ]));

  row.appendChild(makeBadge(f));
  row.appendChild(el('div', { class: 'timing' }, [f.ms ? `${(f.ms / 1000).toFixed(1)}s` : '']));

  if (isFailed) {
    row.appendChild(el('button', {
      class: 'icon-btn',
      title: 'Retry',
      onClick: () => {
        updateFile(f.id, { status: 'pending', error: null });
        renderExtractStage(mounted);
      }
    }, ['↻']));
  } else {
    row.appendChild(el('div', {}));
  }

  return row;
}

function makeSummary(f) {
  if (f.status === 'failed') return f.error || 'Failed.';
  if (f.status === 'done' && f.extracted) {
    const e = f.extracted;
    const amt = typeof e.total_amount === 'number'
      ? new Intl.NumberFormat('en-US', { style: 'currency', currency: e.currency || 'USD' }).format(e.total_amount)
      : '?';
    return `${e.vendor_name || '(no vendor)'} · ${amt}`;
  }
  if (f.status === 'working') return 'Reading invoice…';
  return `${formatBytes(f.size)}${f.pageCount ? ` · ${f.pageCount} page${f.pageCount === 1 ? '' : 's'}` : ''}`;
}

function makeBadge(f) {
  const map = {
    pending: ['pending', 'Pending'],
    working: ['working', 'Working'],
    done:    ['done',    'Extracted'],
    failed:  ['failed',  'Failed']
  };
  const [cls, label] = map[f.status] || map.pending;
  return el('span', { class: `badge ${cls}` }, [el('span', { class: 'dot' }), label]);
}

async function runExtraction() {
  setState({ isExtracting: true });

  try {
    while (!cancelRequested) {
      const state = getState();
      const next = state.files.find(f => f.status === 'pending');
      if (!next) break;

      const apiKey = getSessionApiKey();
      if (!apiKey) {
        toast('API key locked. Unlock to continue.', 'error');
        break;
      }

      updateFile(next.id, { status: 'working' });
      renderExtractStage(mounted);

      try {
        const { data, ms } = await extractInvoice(next.file, apiKey, {
          model: state.settings.model
        });
        updateFile(next.id, { status: 'done', extracted: data, ms, error: null });
      } catch (err) {
        updateFile(next.id, { status: 'failed', error: err.message });
        toast(`Extraction failed: ${next.name}`, 'error', 5000);
      }
      renderExtractStage(mounted);
    }
  } finally {
    setState({ isExtracting: false });

    // Apply vendor rules to anything that just finished
    const s = getState();
    await applyRulesToFiles(s.files, s.settings.matchStrategy, s.settings.matchThreshold);
    setState({ files: s.files });    // re-emit so the UI sees coding
    saveBatchSnapshot().catch(() => {});

    renderExtractStage(mounted);
    const done = s.files.filter(f => f.status === 'done').length;
    if (done > 0 && !s.files.some(f => f.status === 'pending' || f.status === 'working')) {
      toast(`Extraction complete: ${done} of ${s.files.length}.`, 'success');
    }
  }
}
